import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './admin/AdminAuthContext';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import CheckoutPage from './pages/CheckoutPage';
import ProcessingPage from './pages/ProcessingPage';
import ConfirmPage from './pages/ConfirmPage';
import BecomeSellerPage from './pages/BecomeSellerPage';
import SellerApplyPage from './pages/SellerApplyPage';
import SellerAppliedPage from './pages/SellerAppliedPage';
import SellerDashboardPage from './pages/SellerDashboardPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import { LoginPage, RegisterPage } from './pages/AuthPage';
import AdminAuth from './admin/AdminAuth';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminOrders from './admin/AdminOrders';
import AdminProducts from './admin/AdminProducts';
import AdminPending from './admin/AdminPending';
import AdminSellers from './admin/AdminSellers';
import './index.css';

const NO_NAV = ['/', '/login', '/register', '/checkout', '/checkout/processing', '/checkout/confirm'];

function ProtectedRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{ minHeight: '100vh' }} />;
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function AdminProtectedRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <div style={{ minHeight: '100vh', background: '#0A0A0B' }} />;
  if (!admin) return <Navigate to="/admin/auth" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function Layout() {
  const { pathname } = useLocation();
  const isAdmin  = pathname.startsWith('/admin');
  const showNav  = !isAdmin && !NO_NAV.includes(pathname);

  return (
    <>
      {showNav && <Navbar />}
      <Routes>
        {/* Shop routes */}
        <Route path="/"            element={<LandingPage />} />
        <Route path="/home"        element={<HomePage />} />
        <Route path="/shop"        element={<ShopPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/cart"        element={<CartPage />} />
        <Route path="/login"       element={<LoginPage />} />
        <Route path="/register"    element={<RegisterPage />} />
        <Route path="/orders"              element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/orders/:id/track"    element={<ProtectedRoute><OrderTrackingPage /></ProtectedRoute>} />
        <Route path="/profile"             element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/support"             element={<SupportPage />} />
        <Route path="/checkout"            element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/checkout/processing" element={<ProcessingPage />} />
        <Route path="/checkout/confirm"    element={<ConfirmPage />} />
        <Route path="/become-seller"       element={<BecomeSellerPage />} />
        <Route path="/seller/apply"        element={<ProtectedRoute><SellerApplyPage /></ProtectedRoute>} />
        <Route path="/seller/applied"      element={<ProtectedRoute><SellerAppliedPage /></ProtectedRoute>} />
        <Route path="/seller/dashboard"    element={<ProtectedRoute><SellerDashboardPage /></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin/auth"     element={<AdminAuth />} />
        <Route path="/admin"          element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
        <Route path="/admin/orders"   element={<AdminProtectedRoute><AdminOrders /></AdminProtectedRoute>} />
        <Route path="/admin/products" element={<AdminProtectedRoute><AdminProducts /></AdminProtectedRoute>} />
        <Route path="/admin/pending"  element={<AdminProtectedRoute><AdminPending /></AdminProtectedRoute>} />
        <Route path="/admin/sellers"  element={<AdminProtectedRoute><AdminSellers /></AdminProtectedRoute>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <Layout />
            </BrowserRouter>
          </AdminAuthProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
